// sdv-cart.js
// ════════════════════════════════════════════════════════════════════════
// Synchronisation du drawer panier avec Shopify Ajax Cart (/cart.js).
// Remplace l'ancien système localStorage de la maquette.
// Exposé sous window.SDVCart pour usage externe (refresh après add).
// ════════════════════════════════════════════════════════════════════════

(() => {

  let currentCart = null; // dernier état panier connu (pour le bouton Commander)

  // Début de paiement marketplace, publié aux « événements client » Shopify.
  // Shopify n'émet pas `checkout_started` quand le paiement passe par un
  // brouillon de commande (notre cas) : Meta n'a jamais reçu un seul début de
  // paiement. Le pixel personnalisé « SDV Marketplace » s'abonne donc à cet
  // événement, émis au clic sur « Payer » (drawer comme page panier).
  window.addEventListener("sdv:paiement-debut", (e) => {
    const detail = e.detail || {};
    try {
      window.Shopify?.analytics?.publish?.("sdv_paiement_debut", {
        currency: String(detail.currency || "EUR").toUpperCase(),
        items: (detail.items || []).map((item) => ({
          variantId: String(item.variant_id || item.id || ""),
          productId: String(item.product_id || ""),
          title: item.product_title || item.title || "",
          type: item.product_type || "",
          vendor: item.vendor || "",
          quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
          cents: parseInt(item.final_price != null ? item.final_price : item.price, 10) || 0,
        })),
      });
    } catch {
      // La mesure ne doit jamais gêner le paiement.
    }
  });

  function formatMoney(cents) {
    const value = (cents / 100);
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "EUR",
      minimumFractionDigits: 0,
    }).format(value);
  }

  function imgUrl(line) {
    return line.featured_image?.url || line.image || "";
  }

  async function fetchCart() {
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      return await res.json();
    } catch (e) {
      console.error("[SDVCart] fetch error", e);
      return null;
    }
  }

  function escapeHtml(s) {
    return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  function setBusy(on) {
    document.querySelector("[data-cart-list]")?.classList.toggle("is-busy", on);
  }

  function parsePositiveInt(value) {
    const n = Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function lineMaxQty(line) {
    return parsePositiveInt(line?.properties?.["_sdv-max-qty"]);
  }

  function cartQtyForVariant(cart, variantId) {
    return (cart?.items || [])
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  async function assertCanAddVariant(variantId, quantity, maxQuantity) {
    const max = parsePositiveInt(maxQuantity);
    if (!max) return;
    const cart = await fetchCart();
    const alreadyInCart = cartQtyForVariant(cart, variantId);
    if (alreadyInCart + Math.max(1, Number(quantity) || 1) > max) {
      throw new Error(max === 1
        ? SDV_T("cart.uniqueInCart", "Cette pièce unique est déjà dans votre panier.")
        : SDV_T("cart.stockAvailable", `Stock disponible : ${max} exemplaire${max > 1 ? "s" : ""}.`, { n: max }));
    }
  }

  async function removeLine(line) {
    setBusy(true);
    const fd = new FormData();
    fd.append("id", String(line));
    fd.append("quantity", "0");
    await fetch("/cart/change.js", { method: "POST", body: fd, credentials: "same-origin" }).catch(() => {});
    return refresh();
  }

  async function addLine({ id, quantity = 1, properties = {}, maxQuantity = null }) {
    const max = parsePositiveInt(maxQuantity || properties["_sdv-max-qty"]);
    if (max) properties["_sdv-max-qty"] = String(max);
    await assertCanAddVariant(id, quantity, max);
    const fd = new FormData();
    fd.append("id", String(id));
    fd.append("quantity", String(quantity));
    Object.entries(properties).forEach(([k, v]) => fd.append(`properties[${k}]`, v));
    const res = await fetch("/cart/add.js", { method: "POST", body: fd, credentials: "same-origin" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || SDV_T("cart.addError", "Impossible d'ajouter au panier."));
    }
    // Article ajouté : publié pour la mesure publicitaire (pixel ChatGPT Ads).
    const ajout = await res.json().catch(() => null);
    if (ajout) window.dispatchEvent(new CustomEvent("sdv:panier-ajout", { detail: ajout }));
    return refresh();
  }

  async function createMarketplaceCheckout(cart) {
    const items = cart?.items || [];
    const res = await fetch("/apps/sdv-marketplace/cart/checkout", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      // Attribution publicitaire (pixel ChatGPT Ads), seulement si le visiteur a consenti.
      body: JSON.stringify({ items, attribution: typeof window.sdvAttributionAds === "function" ? window.sdvAttributionAds() : undefined }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.invoiceUrl) {
      const error = new Error(data?.erreur || SDV_T("cart.checkoutError", "Impossible de préparer le paiement."));
      error.payload = data;
      error.status = res.status;
      throw error;
    }
    // Début du paiement (mesure publicitaire Meta et ChatGPT Ads), publié une
    // fois le lien de paiement obtenu : publié avant, un refus du serveur
    // (connexion requise, panier invalide…) comptait un paiement qui n'avait
    // pas commencé, puis un second au nouvel essai.
    window.dispatchEvent(new CustomEvent("sdv:paiement-debut", { detail: { items, currency: cart?.currency } }));
    return data;
  }

  async function removeCartItems(items) {
    await Promise.all((items || []).map((item) => {
      const fd = new FormData();
      fd.append("id", String(item.key));
      fd.append("quantity", "0");
      return fetch("/cart/change.js", { method: "POST", body: fd, credentials: "same-origin" });
    }));
  }

  async function marketplaceCheckout() {
    const drawer = document.querySelector("[data-cart-drawer]");
    const checkoutBtn = document.querySelector("[data-cart-checkout]");
    // Panier vide → on renvoie vers la marketplace plutôt qu'un bouton mort.
    if (!currentCart || currentCart.item_count === 0) {
      window.location.href = drawer?.dataset.marketplaceUrl || "/collections/all";
      return;
    }
    // L'ajout au panier est libre ; la connexion est exigée seulement ici, au
    // moment de commander. Invité → page de connexion/inscription Shopify
    // (retour automatique au checkout après authentification).
    const loggedIn = drawer?.dataset.loggedIn === "1";
    if (!loggedIn) {
      const base = drawer?.dataset.loginUrl || "/account/login";
      const sep = base.indexOf("?") === -1 ? "?" : "&";
      window.location.href = `${base}${sep}return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return;
    }
    try {
      if (checkoutBtn) checkoutBtn.disabled = true;
      const checkout = await createMarketplaceCheckout(currentCart);
      // Une demi-seconde au moins avant de quitter la page : le temps que les
      // pixels envoient le début de paiement.
      await Promise.all([removeCartItems(currentCart.items || []), new Promise((r) => setTimeout(r, 500))]);
      window.location.href = checkout.invoiceUrl;
    } catch (error) {
      const payload = error?.payload || {};
      if (payload.besoinConnexion) {
        const base = drawer?.dataset.loginUrl || "/account/login";
        const sep = base.indexOf("?") === -1 ? "?" : "&";
        window.location.href = `${base}${sep}return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        return;
      }
      if (payload.code === "MULTI_SELLER_CART") {
        window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/") + "cart";
        return;
      }
      showToast(payload.erreur || error?.message || SDV_T("cart.checkoutError", "Impossible de préparer le paiement."));
      if (checkoutBtn) checkoutBtn.disabled = false;
    }
  }

  function render(cart) {
    if (!cart) return;
    currentCart = cart;

    const countEl = document.querySelector("[data-cart-count]");
    if (countEl) {
      countEl.textContent = String(cart.item_count);
      countEl.classList.toggle("is-visible", cart.item_count > 0);
    }

    const list = document.querySelector("[data-cart-list]");
    if (list) {
      if (cart.items.length === 0) {
        const drawer = document.querySelector("[data-cart-drawer]");
        const mpUrl = drawer?.dataset.marketplaceUrl || "/collections/mode-vintage";
        const mpLabel = drawer?.dataset.marketplaceLabel || "Découvrir la marketplace";
        list.innerHTML = `
          <div class="cart-drawer__empty">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="20" r="1.6"/><circle cx="17" cy="20" r="1.6"/><path d="M3 4h2l2.5 11.5a2 2 0 0 0 2 1.5h8a2 2 0 0 0 2-1.5L21 8H6"/></svg>
            <p>${SDV_T("cart.empty", "Votre panier est vide.")}<br>${SDV_T("cart.emptyCta", "Ajoutez des pièces depuis la marketplace.")}</p>
            <a class="cart-drawer__empty-cta" href="${escapeHtml(mpUrl)}">${escapeHtml(mpLabel)}</a>
          </div>
        `;
      } else {
        // Regrouper par vendeur (ordre d'apparition)
        const groups = [];
        const idx = {};
        cart.items.forEach((it) => {
          const sellerId = String(it.properties?.["_sdv-seller"] || it.vendor || "vendeur");
          const v = it.vendor || SDV_T("common.seller", "Vendeur");
          if (!(sellerId in idx)) { idx[sellerId] = groups.length; groups.push({ sellerId, vendor: v, items: [] }); }
          groups[idx[sellerId]].items.push(it);
        });

        // Offre acceptée : prix négocié (le serveur le relit en base au paiement).
        const prixLigne = (it) => {
          const offre = Number(it.properties?.["_sdv-offre-prix"]);
          return it.properties?.["_sdv-offre"] && offre > 0 ? offre : it.final_line_price;
        };
        const itemHtml = (it) => `
          <div class="cart-drawer__item">
            <a class="cart-drawer__item-media" href="${escapeHtml(it.url)}"><img src="${imgUrl(it)}" alt="${escapeHtml(it.product_title)}"></a>
            <div class="cart-drawer__item-info">
              <a class="cart-drawer__item-title" href="${escapeHtml(it.url)}">${escapeHtml(it.product_title)}</a>
              <p class="cart-drawer__item-price">${formatMoney(prixLigne(it))}</p>
            </div>
            <button class="cart-drawer__item-remove" data-cart-remove="${it.key}" type="button" aria-label="Retirer">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6l-12 12"/></svg>
            </button>
          </div>
        `;

        list.innerHTML = groups.map((g) => `
          <div class="cart-drawer__group">
            <p class="cart-drawer__group-vendor">Vendu par <strong>${escapeHtml(g.vendor)}</strong></p>
            ${g.items.map(itemHtml).join("")}
            <a class="cart-drawer__seller-checkout" href="${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart?seller=${encodeURIComponent(g.sellerId)}">Commander chez ${escapeHtml(g.vendor)}</a>
          </div>
        `).join("");

        list.querySelectorAll("[data-cart-remove]").forEach((btn) => {
          btn.addEventListener("click", () => removeLine(btn.dataset.cartRemove));
        });
      }
    }

    const totalEl = document.querySelector("[data-cart-total]");
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

    const checkoutBtn = document.querySelector("[data-cart-checkout]");
    if (checkoutBtn) {
      // Le paiement est volontairement déclenché par vendeur depuis chaque bloc.
      checkoutBtn.hidden = cart.item_count > 0;
      checkoutBtn.disabled = cart.item_count > 0;
      checkoutBtn.onclick = cart.item_count > 0 ? null : marketplaceCheckout;
    }
  }

  async function refresh() {
    const cart = await fetchCart();
    render(cart);
    return cart;
  }

  // Toast
  let toastEl = null;
  let toastTimer = null;
  function showToast(message) {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "cart-toast";
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l4 4 10-10"></path></svg>
      <span>${escapeHtml(message)}</span>
    `;
    void toastEl.offsetWidth;
    toastEl.classList.add("is-visible");
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove("is-visible"), 2200);
  }
  function showCartToast(label) { showToast(SDV_T("cart.added", `${label} ajouté au panier`, { label })); }

  function openCartDrawer() {
    if (window.SDVDrawer?.ouvrir) {
      window.SDVDrawer.ouvrir();
      return;
    }
    const drawer = document.querySelector("[data-cart-drawer]");
    if (!drawer) return;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }

  // Bouton "ajouter" sur chaque carte produit (.product-card__cart)
  // S'attend à trouver un data-variant-id sur la carte.
  document.querySelectorAll(".product-card__cart").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const card = btn.closest(".product-card");
      if (!card) return;
      const variantId = card.dataset.variantId || btn.dataset.variantId;
      const maxQuantity = btn.dataset.maxQty || card.dataset.maxQty;
      if (!variantId) {
        console.warn("[SDVCart] No data-variant-id on product card", card);
        return;
      }
      const brand = card.querySelector(".product-card__brand")?.textContent?.trim() ?? "Article";

      try {
        const properties = {};
        const sellerId = card.dataset.sellerId || btn.dataset.sellerId;
        const shippingMode = card.dataset.shippingMode || btn.dataset.shippingMode;
        const shippingTier = card.dataset.shippingTier || btn.dataset.shippingTier;
        if (sellerId) properties["_sdv-seller"] = sellerId;
        if (shippingMode) properties["_sdv-shipping-mode"] = shippingMode;
        if (shippingTier) properties["_sdv-shipping-tier"] = shippingTier;
        if (maxQuantity) properties["_sdv-max-qty"] = maxQuantity;

        await addLine({ id: variantId, properties, maxQuantity });
        btn.classList.add("is-added");
        setTimeout(() => btn.classList.remove("is-added"), 600);

        const countEl = document.querySelector("[data-cart-count]");
        if (countEl) {
          countEl.classList.add("is-pulse");
          setTimeout(() => countEl.classList.remove("is-pulse"), 480);
        }
        showCartToast(brand);
      } catch (err) {
        console.error("[SDVCart] add error", err);
        alert(err.message || SDV_T("cart.addError", "Impossible d'ajouter au panier."));
      }
    });
  });

  document.querySelectorAll("form.product-buybox__form").forEach((form) => {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const variantId = fd.get("id");
      if (!variantId) return;
      const quantity = parsePositiveInt(fd.get("quantity")) || 1;
      const properties = {};
      fd.forEach((value, key) => {
        const match = String(key).match(/^properties\[(.+)]$/);
        if (match) properties[match[1]] = String(value);
      });
      const maxQuantity = form.querySelector("[data-sdv-max-qty]")?.value || properties["_sdv-max-qty"];

      try {
        await addLine({ id: variantId, quantity, properties, maxQuantity });
        showCartToast(SDV_T("cart.item", "Article"));
        openCartDrawer();
      } catch (err) {
        console.error("[SDVCart] product add error", err);
        showToast(err.message || SDV_T("cart.addError", "Impossible d'ajouter au panier."));
      }
    });
  });

  window.SDVCart = { refresh, addLine, removeLine };

  // Rendu initial
  refresh();
})();
